import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { AgentActionService } from "../../services/AgentActionService";
import { Application } from "../../model_gen/application";
import { AgentAction, Agent, AgentActionType } from "../../model_gen/agent_base";
import { AgentService } from "../../services/AgentService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ArrowLeftIcon, Trash2Icon } from "lucide-react";
import { AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import AgentActionEdit from "./AgentActionEdit";
import { ActionReference } from "../../types/AgentAction";

const AgentActionEditPage: React.FC = () => {
  const { agentId, actionId } = useParams<{ agentId: string; actionId: string }>();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [action, setAction] = useState<AgentAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form state
  const [actionType, setActionType] = useState<AgentActionType | "">("");
  const [actionName, setActionName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [actionConfig, setActionConfig] = useState<any>({});
  const [invocationMode, setInvocationMode] = useState<"foreground" | "background">("foreground");
  const [whenToUse, setWhenToUse] = useState<string>("");
  const [whenNotToUse, setWhenNotToUse] = useState<string>("");
  const [references, setReferences] = useState<ActionReference[]>([]);

  // Convert OpenAPI format back to UI-friendly format for editing
  const convertFromOpenAPIFormat = (openAPIConfig: any): any => {
    if (!openAPIConfig || typeof openAPIConfig !== "object") {
      return {};
    }

    // For LLM configs, return as-is (they don't use OpenAPI format)
    if (openAPIConfig.model_config || openAPIConfig.prompt || 
        (openAPIConfig.endpoint === undefined && openAPIConfig.openapi === undefined)) {
      return openAPIConfig;
    }

    // If it's already in UI format (has endpoint, method, etc. directly), return as-is
    if (openAPIConfig.endpoint || openAPIConfig.method || openAPIConfig.headers) {
      return openAPIConfig;
    }

    // If it's an OpenAPI spec, parse it
    if (openAPIConfig.openapi || openAPIConfig.swagger) {
      const uiConfig: any = {
        endpoint: "",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        timeout: 30,
        parameters: [],
        requestSchema: {},
        responseSchemas: {},
        auth: {
          type: "none",
          token: "",
          header: "Authorization",
          key: "",
        }
      };

      // Extract endpoint and method from paths
      if (openAPIConfig.paths && typeof openAPIConfig.paths === "object") {
        const pathEntries = Object.entries(openAPIConfig.paths);
        if (pathEntries.length > 0) {
          const [path, pathItem] = pathEntries[0] as [string, any];
          
          // Reconstruct full URL
          if (openAPIConfig.servers && openAPIConfig.servers[0]?.url) {
            uiConfig.endpoint = openAPIConfig.servers[0].url + path;
          } else {
            uiConfig.endpoint = path;
          }

          // Get the first HTTP method
          const methods = ["get", "post", "put", "patch", "delete"];
          for (const method of methods) {
            if (pathItem[method]) {
              uiConfig.method = method.toUpperCase();
              const operation = pathItem[method];

              // Extract parameters
              if (operation.parameters && Array.isArray(operation.parameters)) {
                uiConfig.parameters = operation.parameters
                  .filter((param: any) => param.in === "path" || param.in === "query")
                  .map((param: any) => ({
                    name: param.name,
                    in: param.in,
                    type: param.schema?.type || "string",
                    required: param.required || false,
                    description: param.description,
                    defaultValue: param.schema?.default,
                    enum: param.schema?.enum
                  }));
              }

              // Extract request body schema
              if (operation.requestBody?.content?.["application/json"]?.schema) {
                uiConfig.requestSchema = convertOpenAPISchemaToUIFormat(
                  operation.requestBody.content["application/json"].schema
                );
              }

              // Extract response schemas
              if (operation.responses && typeof operation.responses === "object") {
                Object.entries(operation.responses).forEach(([statusCode, response]: [string, any]) => {
                  if (response.content?.["application/json"]?.schema) {
                    uiConfig.responseSchemas[statusCode] = convertOpenAPISchemaToUIFormat(
                      response.content["application/json"].schema
                    );
                  }
                });
              }

              break;
            }
          }
        }
      }

      // Extract authentication
      if (openAPIConfig.components?.securitySchemes) {
        const securitySchemes = openAPIConfig.components.securitySchemes;
        if (securitySchemes.bearerAuth?.type === "http" && securitySchemes.bearerAuth?.scheme === "bearer") {
          uiConfig.auth.type = "bearer";
        } else if (securitySchemes.apiKeyAuth?.type === "apiKey") {
          uiConfig.auth.type = "api_key";
          uiConfig.auth.header = securitySchemes.apiKeyAuth.name;
        }
      }

      // Extract base_url if present
      if (openAPIConfig.base_url) {
        uiConfig.base_url = openAPIConfig.base_url;
      }

      // Extract response_type if present
      if (openAPIConfig.response_type) {
        uiConfig.response_type = openAPIConfig.response_type;
      }

      // Extract mock_responses if present
      if (openAPIConfig.mock_responses) {
        uiConfig.mock_responses = openAPIConfig.mock_responses;
      }

      return uiConfig;
    }

    // Return as-is if we can't parse it
    return openAPIConfig;
  };

  // Convert OpenAPI schema to UI SchemaProperty format
  const convertOpenAPISchemaToUIFormat = (schema: any): { [key: string]: any } => {
    if (!schema?.properties) {
      return {};
    }

    const uiProperties: { [key: string]: any } = {};
    
    Object.entries(schema.properties).forEach(([propName, propSchema]: [string, any]) => {
      const uiProp: any = {
        name: propName,
        type: propSchema.type || "string",
        description: propSchema.description || "",
        required: schema.required?.includes(propName) || false
      };

      if (propSchema.format) uiProp.format = propSchema.format;
      if (propSchema.enum) uiProp.enum = propSchema.enum;

      if (propSchema.type === "object" && propSchema.properties) {
        uiProp.properties = convertOpenAPISchemaToUIFormat(propSchema);
      } else if (propSchema.type === "array" && propSchema.items) {
        uiProp.items = {
          name: "item",
          type: propSchema.items.type || "string",
          description: propSchema.items.description || ""
        };
        if (propSchema.items.properties) {
          uiProp.items.properties = convertOpenAPISchemaToUIFormat(propSchema.items);
        }
      }

      uiProperties[propName] = uiProp;
    });

    return uiProperties;
  };

  // Load agent, application, and action details
  useEffect(() => {
    if (agentId && actionId && apiKey) {
      setLoading(true);
      setError(null);

      // Create subscription array to manage all subscriptions
      const subscriptions: any[] = [];

      // Load agent details
      const agentSubscription = AgentService.getAgent(Number(agentId), () => {}, apiKey).subscribe(
        (agent: Agent) => {
          setAgent(agent);
          
          // Load application details
          if (agent.application_id) {
            const appSubscription = ApplicationService.getApplication(agent.application_id, () => {}, apiKey).subscribe(
              (app: Application) => {
                setApplication(app);
              },
              (error: any) => {
                console.error("Error loading application:", error);
                setError("Failed to load application details");
              }
            );
            subscriptions.push(appSubscription);
          }

          // Load action details
          const actionSubscription = AgentActionService.getAction(Number(actionId), () => {}, apiKey).subscribe(
            (action: AgentAction) => {
              setAction(action);
              setActionType(action.action_type || "");
              setActionName(action.action_name || "");
              setDescription(action.description || "");
              setInvocationMode((action as any).invocation_mode || "foreground");
              const meta: any = (action as any).meta || {};
              setWhenToUse(meta.when_to_use || "");
              setWhenNotToUse(meta.when_not_to_use || "");
              
              // Convert OpenAPI format back to UI format for editing
              const uiConfig = convertFromOpenAPIFormat(action.action_config || {});
              setActionConfig(uiConfig);
              
              // Load references from meta.references (AgentAction model's meta field)
              if (meta.references && Array.isArray(meta.references)) {
                setReferences(meta.references);
              } else {
                setReferences([]);
              }
              
              setLoading(false);
            },
            (error: any) => {
              console.error("Error loading action:", error);
              setError("Failed to load action details");
              setLoading(false);
            }
          );
          subscriptions.push(actionSubscription);
        },
        (error: any) => {
          console.error("Error loading agent:", error);
          setError("Failed to load agent details");
          setLoading(false);
        }
      );
      subscriptions.push(agentSubscription);

      // Cleanup function to unsubscribe all subscriptions
      return () => {
        subscriptions.forEach((subscription) => {
          if (subscription && typeof subscription.unsubscribe === "function") {
            subscription.unsubscribe();
          }
        });
      };
    }
  }, [agentId, actionId, apiKey]);

  const handleSubmit = async () => {
    if (!actionType || !actionName || !description || !actionId) {
      setError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const updatedAction: Partial<AgentAction> = {
      action_name: actionName,
      action_type: actionType as AgentActionType,
      description: description,
      action_config: actionConfig,
      // @ts-ignore allow passing through until model_gen regenerates
      invocation_mode: invocationMode,
      meta: {
        ...(whenToUse?.trim() ? { when_to_use: whenToUse.trim() } : {}),
        ...(whenNotToUse?.trim() ? { when_not_to_use: whenNotToUse.trim() } : {}),
        ...(references.length > 0 ? { references: references } : {}),
      },
    };

    AgentActionService.updateAction(Number(actionId), updatedAction, () => {}, apiKey).subscribe(
      () => {
        setIsSubmitting(false);
        navigate(`/agent/${agentId}/actions/${actionId}`);
      },
      (error: any) => {
        console.error("Error updating action:", error);
        setError("Failed to update action. Please try again.");
        setIsSubmitting(false);
      }
    );
  };

  const handleDeleteClick = () => {
    if (!actionId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!actionId) return;

    setDeleteDialogOpen(false);
    setIsDeleting(true);
    setError(null);

    AgentActionService.deleteAction(Number(actionId), () => {}, apiKey).subscribe(
      () => {
        setIsDeleting(false);
        navigate(`/agent/${agentId}`);
      },
      (error: any) => {
        console.error("Error deleting action:", error);
        setError("Failed to delete action. Please try again.");
        setIsDeleting(false);
      }
    );
  };

  const handleCancel = () => {
    navigate(`/agent/${agentId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-maitai-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading action details...</p>
        </div>
      </div>
    );
  }

  if (error && !action) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <div>
                <h3 className="font-medium text-red-800 dark:text-red-200">Error Loading Action</h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Button variant="outline" onClick={handleCancel}>
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleCancel} className="gap-2">
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Agent
          </Button>
          <div>
            <h1 className="text-2xl font-gc-med text-foreground">Edit Action</h1>
            <p className="text-muted-foreground">
              {agent?.agent_name ? (
                <>Edit the configuration for this {agent.agent_name} agent action</>
              ) : (
                <>Edit the configuration for this agent action</>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDeleteClick}
            disabled={isDeleting || isSubmitting}
            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <Trash2Icon className="w-4 h-4" />
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Action Configuration */}
      <AgentActionEdit
        actionType={actionType as "api_call" | "llm_call" | "webhook_creation" | "maitai_workflow" | undefined}
        config={actionConfig}
        onChange={setActionConfig}
        actionName={actionName}
        description={description}
        onActionNameChange={setActionName}
        onDescriptionChange={setDescription}
        onActionTypeChange={(type: string) => setActionType(type as AgentActionType)}
        applicationId={application?.id}
        showSaveActions={true}
        onSave={handleSubmit}
        onCancel={handleCancel}
        isSaving={isSubmitting || isDeleting}
        isValid={!!actionType && !!actionName && !!description}
        invocationMode={invocationMode}
        onInvocationModeChange={setInvocationMode}
        whenToUse={whenToUse}
        whenNotToUse={whenNotToUse}
        onWhenToUseChange={setWhenToUse}
        onWhenNotToUseChange={setWhenNotToUse}
        references={references}
        onReferencesChange={setReferences}
      />

      {/* Delete Action Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this action?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this action? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AgentActionEditPage; 